<template>
  <UploadFile
    :label="label"
    :filename="videoName"
    :disabled="androidWatcfaceOS == 'vela' || attrDisabled == 'all' || disabled"
    :placeholder="'请上传mp4文件'"
    :tooltip="
      (androidWatcfaceOS == 'not-android-device'
        ? ''
        : '在智能设备上展示视频') +
      (androidWatcfaceOS !== 'not-android-device' && isMulti ? '，' : '') +
      (isMulti ? '最多支持上传2个视频' : '')
    "
    @upload="onAddVideo"
    @delete="onDeleteVideo"
  />
  <modal-video-transform ref="modal" @ok="onVideoOk" />
</template>

<script lang="ts" setup>
import { useWatchfaceAndroid } from '@/hooks';
import { ElectronApi } from '@/services/electron.api';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { pickFile } from '@/utils/fs/pick-file.utils';
import { getMediainfo } from '@/utils/fs/video.utils';
import { Modal } from 'ant-design-vue';
import { Track, VideoTrack } from 'mediainfo.js';
import { computed, ref } from 'vue';

const props = defineProps({
  label: {
    type: String,
    default: '上传视频'
  },
  videoName: {
    type: String,
    required: true
  },
  /**
   * 视频时长,单位秒
   */
  duration: {
    type: Number,
    required: false,
    default: 0
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const emits = defineEmits<{
  (event: 'delete'): void;
  (
    event: 'upload',
    data: {
      videoSrc: string;
      videoName: string;
      videoFps: number;
      w: number;
      h: number;
    }
  ): void;
}>();
const editorStore = useEditorStore();
const watchfaceStore = useWatchfaceStore();
const attrDisabled = computed(() => editorStore.attrDisabled);
const { androidWatcfaceOS } = useWatchfaceAndroid();
const isMulti = computed(() => canUse('multi-sprite-video').valid);
const onDeleteVideo = () => {
  emits('delete');
};

const onAddVideo = async () => {
  pickThenUploadVideo();
};

const onVideoOk = (data: {
  videoSrc: string;
  videoName: string;
  videoFps: number;
  w: number;
  h: number;
}) => {
  const { videoSrc, videoName, videoFps, w, h } = data;
  emits('upload', {
    videoSrc,
    videoName,
    videoFps,
    w,
    h
  });
};

const validVideo = (track: Track[]) => {
  const videoTrack = track.find(
    (item) => item['@type'] === 'Video'
  ) as VideoTrack;

  if (!videoTrack) {
    Modal.error({
      title: `上传失败`,
      content: `资源有误`
    });
    return false;
  }

  // 如果是自定义视频，需要进一步校验
  const {
    Width: videoWidth = 0,
    Height: videoHeight = 0,
    FrameRate = 0,
    Duration = 0
  } = videoTrack;

  let deviceWidth = watchfaceStore.size.width;
  let deviceHeight = watchfaceStore.size.height;
  deviceWidth = deviceWidth == 464 ? 466 : deviceWidth;
  deviceHeight = deviceHeight == 464 ? 466 : deviceHeight;
  if (deviceWidth < videoWidth || deviceHeight < videoHeight) {
    Modal.error({
      title: `分辨率过大（${videoWidth}*${videoHeight}）`,
      content: `视频分辨率不可超过设备分辨率：${deviceWidth}*${deviceHeight}`
    });
    return false;
  }

  if (props.duration && Duration > props.duration) {
    Modal.error({
      title: `视频过长（长度为${Duration}s）`,
      content: '视频长度不可超过' + props.duration + '秒'
    });
    return false;
  }

  if (FrameRate > 30) {
    Modal.error({
      title: `帧率过高（帧率为${FrameRate}帧）`,
      content: '视频帧率过高不可超过30'
    });
    return false;
  }

  return true;
};

// 视频转码Modal
const modal = ref();

const pickThenUploadVideo = async () => {
  const { path } = ElectronApi;
  if (!path) return;

  // 选择mp4文件
  const videoFile = await pickFile({ extensions: ['mp4'] })?.then(
    (r) => r?.[0]
  );
  if (!videoFile) return;

  // 获取视频track并进行验证
  const track = (await getMediainfo(videoFile)) ?? [];
  // 验证视频资源
  if (!validVideo(track)) return;

  // 转换视频
  modal.value?.open?.({
    videoFile,
    track
  });
};
</script>
